import React from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography,
} from '@mui/material';
import type {
  EstimateEvidence, EstimateRevision, EstimateRevisionWorkItem,
} from '../api/constructionPriceEstimatorFunctions';
import { formatConstructionMoney } from './constructionPriceMoney';

type Range = { low?: unknown; base?: unknown; high?: unknown };
type DisplayItem = {
  work_item_id?: string;
  components?: Record<string, Range>;
  total?: Range;
};
type DisplayCalculation = {
  display?: {
    work_items?: DisplayItem[];
    totals?: Record<string, Range>;
    adjustments?: Array<{ name?: string; sequence?: number; amount?: Range }>;
  };
  display_policy?: { currency?: string; rounding?: string; allocation?: string };
};

const readable = (value: unknown): string => {
  if (typeof value !== 'string') return 'Not reported';
  const trimmed = value.trim();
  if (!trimmed || /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/i.test(trimmed)) {
    return 'Not reported';
  }
  return trimmed;
};

const date = (value: unknown): string => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(value)) {
    return 'Not reported';
  }
  const parsed = new Date(`${value.slice(0, 10)}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? 'Not reported'
    : new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(parsed);
};

const range = (value: unknown): Range => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Range : { low: value, base: value, high: value }
);

const itemRange = (item: EstimateRevisionWorkItem): Range => {
  const nested = range(item.total);
  return {
    low: nested.low ?? item.low,
    base: nested.base ?? item.base,
    high: nested.high ?? item.high,
  };
};

const componentValue = (item: EstimateRevisionWorkItem, key: string): unknown => {
  const direct = key === 'materials' ? item.material : item[key];
  const saved = direct ?? item.cost_components?.[key === 'materials' ? 'material' : key];
  return range(saved).base;
};

const itemEvidence = (item: EstimateRevisionWorkItem): EstimateEvidence[] => {
  if (Array.isArray(item.evidence)) {
    return item.evidence.filter((value): value is EstimateEvidence => (
      Boolean(value) && typeof value === 'object'
    ));
  }
  return item.evidence && typeof item.evidence === 'object'
    ? [item.evidence] : [];
};

const publicMessage = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const message = value.trim();
  if (!message || !/\s/.test(message) || /(?:private|internal|stack|hash|adapter|policy|snapshot[_ -]?id)/i.test(message)) {
    return null;
  }
  return message;
};

const publicUnit = (value: unknown): string | null => {
  const units: Record<string, string> = {
    square_foot: 'square foot', square_yard: 'square yard',
    cubic_yard: 'cubic yard', linear_foot: 'linear foot',
    each: 'each', hour: 'hour', ton: 'ton', lump_sum: 'lump sum',
  };
  return typeof value === 'string' && typeof units[value] === 'string' ? units[value] : null;
};
const geographyFits: Record<string, string> = {
  exact: 'Exact match', county: 'County match', state: 'State match',
};
const freshnessLabels: Record<string, string> = {
  current: 'Current', stale: 'Stale', not_available: 'Not available',
};

const Evidence: React.FC<{ item: EstimateRevisionWorkItem }> = ({ item }) => {
  const evidence = itemEvidence(item);
  return <Box component="section" role="region" aria-label={`Provenance details for ${item.description}`}>
    {evidence.length ? evidence.map((record, index) => <Box key={index} sx={{ mt: 0.5 }}>
      <Typography variant="body2">{record.source === 'licensed_provider'
        ? 'Licensed construction cost provider'
        : record.source === 'manual_allowance' ? 'User allowance' : 'Approved price source'}</Typography>
      {record.effective_date && <Typography variant="body2">Effective date: {date(record.effective_date)}</Typography>}
      {record.retrieved_at && <Typography variant="body2">Retrieved: {date(record.retrieved_at)}</Typography>}
      {record.geography && <Typography variant="body2">Geography: {readable(record.geography)}</Typography>}
      {publicUnit(record.source_unit) && <Typography variant="body2">Source unit: {publicUnit(record.source_unit)}</Typography>}
      {publicUnit(record.priced_unit) && <Typography variant="body2">Priced unit: {publicUnit(record.priced_unit)}</Typography>}
      {record.geography_fit && typeof geographyFits[record.geography_fit] === 'string' && <Typography variant="body2">Geography fit: {geographyFits[record.geography_fit]}</Typography>}
      {record.freshness_status && typeof freshnessLabels[record.freshness_status] === 'string' && <Typography variant="body2">Freshness: {freshnessLabels[record.freshness_status]}</Typography>}
      {publicMessage(record.transformation) && <Typography variant="body2">{publicMessage(record.transformation)}</Typography>}
    </Box>) : <Typography variant="body2">{item.evidence_type === 'manual_allowance'
      ? 'User allowance; no external source record applies.'
      : 'No public line-level provenance is available.'}</Typography>}
  </Box>;
};

const ItemDetails: React.FC<{ item: EstimateRevisionWorkItem }> = ({ item }) => {
  const quantity = range(item.quantity);
  const low = item.quantity_low ?? quantity.low ?? quantity.base;
  const high = item.quantity_high ?? quantity.high ?? quantity.base;
  const base = quantity.base;
  const unit = typeof item.unit === 'string'
    ? item.unit.replace(/_/g, ' ') : 'unit not reported';
  const reasons = Array.isArray(item.confidence?.reasons)
    ? item.confidence.reasons : [];
  const warnings = Array.isArray(item.warnings) ? item.warnings : [];
  const uncertainty = Array.isArray(item.uncertainty_provenance)
    ? item.uncertainty_provenance : [];
  return <Box component="section" role="region" aria-label={`Line details for ${item.description}`}>
    <Typography variant="body2">{String(low ?? 'Not reported')} / {String(base ?? 'Not reported')} / {String(high ?? 'Not reported')} {unit} (low / base / high)</Typography>
    <Typography variant="body2">Confidence: {readable(item.confidence?.band)}</Typography>
    {item.cost_data?.pricing_kind === 'installed_work' && <Typography variant="body2">Installed bid-tab prices; components not separately reported</Typography>}
    {item.cost_data?.pricing_kind === 'unresolved' && <Typography variant="body2">No eligible item-level cost evidence is available for this item.</Typography>}
    {[...reasons, ...uncertainty, ...warnings].map(publicMessage).filter((value): value is string => Boolean(value))
      .map((message, index) => <Typography key={index} variant="body2">{message}</Typography>)}
    <Evidence item={item} />
  </Box>;
};

export default function ConstructionPriceLegacyEvidence({ revision }: {
  revision: EstimateRevision;
}): React.ReactElement {
  const calculation = revision.calculation as DisplayCalculation | undefined;
  const installed = revision.work_items?.some((item) => item.cost_data?.pricing_kind === 'installed_work');
  const display = installed ? undefined : calculation?.display;
  const currency = revision.currency;
  const money = (value: unknown): string => formatConstructionMoney(value, currency);
  const totals = revision.totals ?? {};
  const totalRows: Array<[string, Range]> = [
    ['Direct costs', display?.totals?.direct ?? range(totals.direct)],
    ['Allowances', display?.totals?.allowances ?? range(totals.allowances)],
    ['Adjustments', display?.totals?.adjustments ?? range(totals.adjustments)],
    ['Final total', display?.totals?.final ?? {
      low: totals.low, base: totals.base, high: totals.high,
    }],
  ];
  return <Box>
    <Typography variant="h6" component="h3">Saved revision evidence</Typography>
    {revision.confidence?.band && <Typography>Confidence: {readable(revision.confidence.band)}</Typography>}
    {calculation?.display_policy && <Typography variant="caption">
      Display rounding: {calculation.display_policy.currency ?? currency} cents, {
        calculation.display_policy.rounding === 'ROUND_HALF_UP'
          ? 'round half up' : 'server rounding'}, {
        calculation.display_policy.allocation === 'largest_remainder_stable_order'
          ? 'largest remainder allocation' : 'server allocation'}.
    </Typography>}
    {[...(revision.confidence?.reasons ?? []), ...(revision.warnings ?? []),
      ...(revision.degradation?.reasons ?? []),
      ...(revision.assumptions ?? []), ...(revision.exclusions ?? [])]
      .map(publicMessage).filter((value): value is string => Boolean(value))
      .map((message, index) => <Typography key={index} variant="body2">{message}</Typography>)}
    <Box component="section" role="region" aria-label="Reconciled estimate totals" sx={{ mt: 1 }}>
      <TableContainer><Table size="small" aria-label="Reconciled low, base, and high estimate totals">
        <TableHead><TableRow><TableCell>Cost category</TableCell><TableCell align="right">Low</TableCell><TableCell align="right">Base</TableCell><TableCell align="right">High</TableCell></TableRow></TableHead>
        <TableBody>{totalRows.map(([label, values]) => <TableRow key={label}>
          <TableCell component="th" scope="row">{label}</TableCell>
          <TableCell align="right">{money(values.low)}</TableCell>
          <TableCell align="right">{money(values.base)}</TableCell>
          <TableCell align="right">{money(values.high)}</TableCell>
        </TableRow>)}</TableBody>
      </Table></TableContainer>
    </Box>
    {(revision.work_items?.length ?? 0) > 0 && <TableContainer sx={{ mt: 1 }}>
      <Table size="small" aria-label="Itemized construction estimate">
        <TableHead><TableRow><TableCell>Work item</TableCell><TableCell align="right">Labor</TableCell><TableCell align="right">Material</TableCell><TableCell align="right">Equipment</TableCell><TableCell align="right">Subcontract / allowance</TableCell><TableCell align="right">Low</TableCell><TableCell align="right">Base</TableCell><TableCell align="right">High</TableCell></TableRow></TableHead>
        <TableBody>{revision.work_items?.map((item, index) => {
          const id = String(item.work_item_id ?? item.id);
          const shown = display?.work_items?.find((row) => row.work_item_id === id);
          const allIn = item.cost_data?.pricing_kind === 'installed_work';
          const unavailable = item.cost_data?.pricing_kind === 'unresolved';
          const actual = itemRange(item);
          const states = item.cost_data?.component_states;
          const components = (['labor', 'materials', 'equipment', 'subcontract'] as const)
            .map((key) => {
              const state = states?.[key === 'materials' ? 'material' : key];
              if (unavailable || state === 'not_available') return 'Price not available';
              if (allIn || state === 'included_in_installed_work'
                || state === 'not_separately_reported') return '—';
              return money(shown?.components?.[key]?.base ?? componentValue(item, key));
            });
          const values = unavailable ? ['Price not available', 'Price not available', 'Price not available']
            : [money(allIn ? actual.low : shown?.total?.low ?? actual.low),
              money(allIn ? actual.base : shown?.total?.base ?? actual.base),
              money(allIn ? actual.high : shown?.total?.high ?? actual.high)];
          return <React.Fragment key={`${id}-${index}`}>
            <TableRow><TableCell component="th" scope="row">{item.description}</TableCell>
              {[...components, ...values].map((value, cell) => <TableCell key={cell} align="right">{value}</TableCell>)}
            </TableRow>
            <TableRow><TableCell colSpan={8}><ItemDetails item={item} /></TableCell></TableRow>
          </React.Fragment>;
        })}</TableBody>
      </Table>
    </TableContainer>}
    {(revision.adjustments?.length ?? 0) > 0 && <Box sx={{ mt: 1 }}>
      <Typography fontWeight={700}>Named adjustments</Typography>
      {revision.adjustments?.map((adjustment, index) => {
        const matching = display?.adjustments?.filter((row) => row.name === adjustment.name
          && (adjustment.sequence === undefined || row.sequence === adjustment.sequence));
        const shown = matching?.length === 1 ? matching[0].amount : undefined;
        const amount = range(adjustment.amount);
        return <Typography key={index} variant="body2">{readable(adjustment.name)}: {
          money(shown?.base ?? amount.base ?? adjustment.base_amount)
        } base · {money(shown?.low ?? amount.low ?? adjustment.low)} low · {
          money(shown?.high ?? amount.high ?? adjustment.high)} high</Typography>;
      })}
    </Box>}
  </Box>;
}
